import React, { useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { ThemeProvider } from '@material-ui/core/styles'
import rebaTheme from '../../themes/rebaTheme'
import logo from '../../../assets/images/landing_page/thumbnail-b358bde6e459b420c0a3f52243762cb3.png'
import defaultAvatar from '../../../assets/images/avatar-placeholder.png'
import {
  passMatch,
  passMinChar,
  validPostalCode,
  validEmail,
  noBasicInfoErrors,
  isWeMeanBusiness
} from '../helpers/validators'
import { sendSegmentAnalytics } from '../helpers/segmentTracking'
import BasicInfoForm from '../components/BasicInfoForm'
import RegistrationSubmitted from '../components/RegistrationSubmitted'
import EmailVerified from '../components/EmailVerified'
import ConfirmationAnimation from '../components/ConfirmationAnimation'
import AccountAuthorized from '../components/AccountAuthorized'
import AvatarCropper from '../components/AvatarCropper'
import Api from '../../Api'
import {
  Button,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Dialog,
  DialogContent
} from '@material-ui/core'

const useStyles = makeStyles((rebaTheme) => ({
  stepper: {
    backgroundColor: 'whitesmoke',
    width: '80vw',
    marginLeft: 'auto',
    marginRight: 'auto'
  },
  header: {
    paddingTop: '15px',
    textAlign: 'center',
    marginBottom: '10px'
  },
  headerText: {
    fontSize: '48px'
  },
  stepContent: {
    marginLeft: 'auto',
    marginRight: 'auto',
    width: '80vw',
    maxWidth: '800px',
    marginTop: '20px'
  },
  logo: {
    maxHeight: '40px',
    height: '10vw'
  }
}))

const RegistrationContainer = () => {
  const classes = useStyles()
  const [step, setStep] = useState(gon.enrollment.step)
  const [user, setUser] = useState(gon.enrollment.user)
  const [fields, setFields] = useState({})
  const [errors, setErrors] = useState({})
  const [showStepThree, setShowStepThree] = useState(false)
  const [isCropping, setIsCropping] = useState(false)

  useEffect(() => {
    setDefaultFields()
  }, [])

  useEffect(() => {
    handleUrlChange()
    handleDisplayVerified()
    sendSegmentAnalytics('Viewed', step, user, fields)
  }, [step])

  const handleUrlChange = () => {
    var newUrl = window.location.origin
      + window.location.pathname
      + '?step='
      + step

    window.history.pushState({path:newUrl}, '', newUrl)
  }

  const nextStep = () => {
    setStep(step + 1)
  }

  const setDefaultFields = () => {
    if (step === 1 && isWeMeanBusiness(gon)) {
      setFields({...fields, ['registration_flow']: 'international_interest'})
    }

    if (step === 1 && gon.invitation) {
      setFields(
        {
          ...fields,
          ['email']: gon.invitation.email,
          ['notes']: gon.invitation.organization.name,
          ['token']: gon.invitation.token
        }
      )
    }

    if (step === 4) {
      setFields(
        {
          ...fields,
          ['country_code']: 'us',
          ['avatar']: defaultAvatar
        }
      )
    }
  }

  const handleChange = (e) => {
    setFields({...fields, [e.target.name]: e.target.value})
  }

  const handleAvatar = (file) => {
    setFields({...fields, ['avatar']: file})
  }

  const handleCrop = (boolean) => {
    setIsCropping(boolean)
  }

  const handlePhone = (number, country) => {
    setFields(
      {
        ...fields,
        ['phone']: number,
        ['country_code']: country.countryCode
      }
    )
  }

  const handlePronouns = (e) => {
    const name = e.target.name
    const value = e.target.value

    if (name === 'pronouns' && value !== 'custom') {
      fields.custom_pronouns = ''
    }

    setFields({...fields, [name]: value})
  }

  const handleDisplayVerified = () => {
    if (gon.enrollment.display_verified) {
      setTimeout(() => {
        setShowStepThree(true)
      }, 2300)
    } else {
      setShowStepThree(true)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (noBasicInfoErrors(fields)) {
      setErrors({})
      sendSegmentAnalytics('Submitted', step, user, fields)
      Api.postNewUser(fields).then((data) => {
        if (data.errors) {
          setErrors({'invalidEmail': data.errors})
        } else {
          setUser(data.user)
          if (gon.invitation) {
            location.reload()
          } else {
            nextStep()
          }
        }
      })
    } else if (!validEmail(fields)) {
      setErrors({'invalidEmail': 'Invalid email address format'})
    } else if (!passMinChar(fields)) {
      setErrors({'passMinCharError': true})
    } else {
      setErrors({'passMatchError': true})
    }
  }

  const handleUpdate = (e) => {
    e.preventDefault()
    if (validPostalCode(fields, setErrors)) {
      setErrors({})
      fields.enrollment = 'complete'
      if (fields.avatar === defaultAvatar) fields.avatar = null
      sendSegmentAnalytics('Submitted', step, user, fields)
      setUser({...user, ['enrollment']: 'complete'})
      nextStep()
      Api.updateProfileDetails(fields, user.id).then(() => {
        setTimeout(() => {
          window.location.href='/'
        }, 2400)
      })
    }
  }

  const getStepContent = () => {
    switch(step) {
      case 1:
        return(
          <>
            <BasicInfoForm
              handleChange={handleChange}
              handleSubmit={handleSubmit}
              fields={fields}
              errors={errors}
              international={isWeMeanBusiness(gon)}
            />
          </>
        )
      case 2:
        return(
          <RegistrationSubmitted
            userName={user.first_name}
            userEmail={user.email}
          />
        )
      case 3:
        return(
          <>
            <Dialog open={!showStepThree}>
              <DialogContent>
                <ConfirmationAnimation message={'Email verified!'}/>
              </DialogContent>
            </Dialog>
            {showStepThree && <EmailVerified userName={user.first_name} />}
          </>
        )
      case 4:
        return(
          <>
            <AccountAuthorized
              userName={user.first_name}
              handleSubmit={handleUpdate}
              handleChange={handleChange}
              fields={fields}
              handlePhone={handlePhone}
              handleAvatar={handleAvatar}
              avatar={fields.avatar}
              setIsCropping={handleCrop}
              errors={errors}
              handlePronouns={handlePronouns}
            />
            <Dialog open={isCropping}>
              <DialogContent>
                <AvatarCropper
                  inputImage={fields.avatar}
                  setIsCropping={handleCrop}
                  handleAvatar={handleAvatar}
                />
              </DialogContent>
            </Dialog>
          </>
        )
      case 5:
        return(
          <Dialog open>
            <DialogContent>
              <ConfirmationAnimation
                message={'Registration process complete!'}
                isComplete={true}
              />
            </DialogContent>
          </Dialog>
        )
    }
  }

  const StepperLabels = () => {
    return(
      <Stepper activeStep={step - 1} className={classes.stepper}>
        <Step><StepLabel>Basic Info</StepLabel></Step>
        <Step><StepLabel>Verify Email</StepLabel></Step>
        <Step><StepLabel>Wait for Approval</StepLabel></Step>
        <Step><StepLabel>Complete Profile</StepLabel></Step>
      </Stepper>
    )
  }

  const RegistrationHeader = () => {
    return(
      <div className={classes.header}>
        <img src={logo} className={classes.logo}/>
        <Typography className={classes.headerText}>REGISTRATION</Typography>
      </div>
    )
  }

  return(
    <ThemeProvider theme={rebaTheme}>
      {step > 2 &&
        <>
          <RegistrationHeader />
          {showStepThree && <StepperLabels />}
        </>
      }
      <div className={classes.stepContent}>{getStepContent()}</div>
    </ThemeProvider>
  )
}

export default RegistrationContainer
